import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { TaskAssignmentDTO } from "@easyshop/shared";
import { isTaskOpen } from "@easyshop/shared";
import { Empty, ErrorState, Loading } from "@/components/ui";
import { TaskCard } from "@/components/messaging/MessageBubble";
import { IosChips, MobileActionButton } from "@/components/mobile/MobileUi";
import { useSession } from "@/hooks/useSession";
import { useSelectedBoutique } from "@/hooks/useSelectedBoutique";
import { useUi } from "@/hooks/useUi";
import { useBoutiqueTasks } from "@/lib/queries";

type Filter = "a_faire" | "retard" | "faites" | "toutes";

const MATCH: Record<Filter, (a: TaskAssignmentDTO) => boolean> = {
  a_faire: (a) => isTaskOpen(a.status),
  retard: (a) => isTaskOpen(a.status) && a.late,
  faites: (a) => !isTaskOpen(a.status),
  toutes: () => true,
};

/** Rang d'affichage : en retard, puis ouvertes, puis faites. */
const rank = (a: TaskAssignmentDTO) => (isTaskOpen(a.status) ? (a.late ? 0 : 1) : 2);

/** Tâches mobile : tâches de la boutique choisie, avec leurs actions (« ✓ Fait », photo…). */
export function MobileTasks() {
  const { can } = useSession();
  const { selectedId } = useSelectedBoutique();
  const { openCompose } = useUi();
  const q = useBoutiqueTasks(selectedId ?? undefined);
  const [params] = useSearchParams();
  const [filter, setFilter] = useState<Filter>(params.get("filtre") === "retard" ? "retard" : "a_faire");

  const tasks = useMemo(
    () =>
      [...(q.data ?? [])].sort(
        (a, b) =>
          rank(a) - rank(b) ||
          // Échéance la plus proche d'abord ; sans échéance en fin.
          (a.task.dueAt ?? "9999").localeCompare(b.task.dueAt ?? "9999")
      ),
    [q.data]
  );
  const count = (f: Filter) => tasks.filter(MATCH[f]).length;
  const shown = tasks.filter(MATCH[filter]);

  return (
    <div className="flex flex-col gap-3">
      {can("messages.send") && selectedId && (
        <MobileActionButton onClick={() => openCompose({ kind: can("tasks.create") ? "tache" : "message", boutiqueIds: [selectedId] })}>
          + {can("tasks.create") ? "Nouvelle tâche / message" : "Nouveau message"}
        </MobileActionButton>
      )}
      <IosChips
        options={[
          { key: "a_faire", label: `À faire (${count("a_faire")})` },
          { key: "retard", label: `En retard (${count("retard")})` },
          { key: "faites", label: `Faites (${count("faites")})` },
          { key: "toutes", label: "Toutes" },
        ]}
        value={filter}
        onChange={setFilter}
      />
      {q.isLoading && <Loading />}
      {q.error && <ErrorState error={q.error} />}
      {q.data && shown.length === 0 && (
        <Empty>{tasks.length === 0 ? "Aucune tâche pour cette boutique." : "Aucune tâche dans ce filtre."}</Empty>
      )}
      {shown.map((a) => (
        <TaskCard key={a.id} a={a} compact ios />
      ))}
    </div>
  );
}
